import { useReducer, useCallback, useRef } from 'react'

const MIN_LEVEL = 1
const MAX_LEVEL = 5
const DEFAULT_LEVEL = 3

const INITIAL_STATE = {
  isOpen: false,
  isSubmitting: false,
  type: '',
  name: '',
  id: 0,
  level: DEFAULT_LEVEL,
}

const ACTIONS = {
  OPEN: 'OPEN',
  SET_LEVEL: 'SET_LEVEL',
  CONFIRM: 'CONFIRM',
  CLOSE: 'CLOSE',
  RESET_SUBMIT: 'RESET_SUBMIT',
}

const modalReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.OPEN:
      return {
        ...INITIAL_STATE,
        isOpen: true,
        type: action.payload.type,
        name: action.payload.name,
        id: action.payload.id,
        level: DEFAULT_LEVEL,
      }
    case ACTIONS.SET_LEVEL:
      return { ...state, level: action.payload }
    case ACTIONS.CONFIRM:
      return { ...state, isSubmitting: true }
    case ACTIONS.CLOSE:
      return INITIAL_STATE
    case ACTIONS.RESET_SUBMIT:
      return { ...state, isSubmitting: false }
    default:
      return state
  }
}

/**
 * Custom hook that encapsulates the add-skill modal state and transitions.
 * Returns the modal state and handler functions for open, rate, confirm, close.
 *
 * The confirm handler is async-safe: it keeps isSubmitting=true until the
 * callback resolves (or rejects), preventing double-submit even when
 * the parent callback is asynchronous.
 *
 * @param {Object} callbacks - Object with onAdd functions keyed by skill type
 * @param {Function} callbacks.technical - fn(id, level) for technical skills
 * @param {Function} callbacks.soft - fn(id, level) for soft skills
 * @param {Function} callbacks.language - fn(id, level) for languages
 * @returns {Object} { state, openModal, handleRate, handleConfirm, handleClose }
 */
const useSkillModal = (callbacks) => {
  const [state, dispatch] = useReducer(modalReducer, INITIAL_STATE)

  // Keep a stable ref to callbacks so handlers don't depend on the object identity
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  const openModal = useCallback((type, name, id) => {
    dispatch({ type: ACTIONS.OPEN, payload: { type, name, id } })
  }, [])

  const handleRate = useCallback((_e, data) => {
    dispatch({ type: ACTIONS.SET_LEVEL, payload: data.rating })
  }, [])

  const handleConfirm = useCallback(async () => {
    if (state.isSubmitting) return
    if (state.level < MIN_LEVEL || state.level > MAX_LEVEL) return

    dispatch({ type: ACTIONS.CONFIRM })

    const addFn = callbacksRef.current[state.type]
    if (addFn) {
      try {
        await addFn(state.id, state.level)
      } catch (err) {
        // Callback failed — reset submitting state without closing
        dispatch({ type: ACTIONS.RESET_SUBMIT })
        return
      }
    }

    dispatch({ type: ACTIONS.CLOSE })
  }, [state.isSubmitting, state.level, state.type, state.id])

  const handleClose = useCallback(() => {
    if (state.isSubmitting) return
    dispatch({ type: ACTIONS.CLOSE })
  }, [state.isSubmitting])

  return { state, openModal, handleRate, handleConfirm, handleClose }
}

export default useSkillModal
